// Cross-dataset set-reference inlining — the single whole-AST pass that
// replaces every row-independent dataset reference in a script with an inline
// literal value/vector, BEFORE evaluation.
//
// Two reference shapes are resolved, both row-independent constants under the
// resolve-once model (a correlated reference is a hard error):
//
//   - a NAMED foreign column `(col other-ds c)` whose head names a bound
//     dataset — resolved through `col`'s own apply, projected, and reduced to
//     the column's bounded distinct value set;
//   - an inline SUB-QUERY operand `(query …)` / `(col (query …) c)` — a
//     dataset-valued sub-expression materialised once and collapsed to the
//     literal vector to inline (provider `collapseRef` shaping when inside a
//     source op, else the generic single-column / explicit-projection rule).
//
// THE OVERLOAD. `(col …)` is syntactically overloaded. In a VALUE expression
// it is a value reference (`(col other-ds c)` → that column's values); in a
// STRUCTURAL position it is a column IDENTIFIER (`join`'s `:on [(col a x)
// (col b x)]`, where the leading label names the join input, not a dataset to
// inline). Resolving the latter would turn a join key into a literal vector
// and break the join. So the pass must know which region it is in.
//
// THE INVARIANT. A value expression is exactly a row-function (`fn`) BODY.
// Predicates, projectors, sort keys, aggregate inputs — every value context in
// the language — is an `fn` body; everything else (op pipelines, source-op
// trailing forms, join structure) is the structural skeleton. So the AST has
// two regions that ALTERNATE at exactly two syntactic boundaries:
//
//   structural ──(enter an `fn` body)──▶ value
//   value ──(enter a source op `(query …)`/`(datapoints …)`)──▶ structural
//
// The pass is a two-state walk over those regions: `descend` traverses the
// structural skeleton and never resolves a `(col …)`; `resolveExpr` resolves
// every reference within a value expression. The STATE is which function the
// walk is in — there is no "am I in a value position" flag. `descend` hands an
// `fn` body to `resolveExpr` (the only way to enter value state); `resolveExpr`
// hands a nested source op back to `descend` (a sub-query's own pipeline is
// structural again, until ITS `fn`). A join key sits in the skeleton and is
// never reached by `resolveExpr`; a sub-query inside a join operand's own
// filter still resolves, because `descend` reaches that operand's `fn`.
//
// `resolveExpr` threads a row-alias STACK (each `fn` pushes its params; an
// inner lambda shadows an outer alias of the same name) and the enclosing
// source op's PROVIDER (for the provider-scoped value collapse). Run once per
// top-level form by `evalScript` (after the static type-check, before the eval
// reduce), so every op's `apply` and every replay PlanNode sees only literals.
// `=`/`!=` against a set operand is forgiven to `in`/`not in` membership.

import { SET_REF_MAX } from '#core/lib/cognite/view.ts';
import type { InstanceIdType } from '#core/schema/defs.ts';

import type { SourcePos } from '../ast.ts';
import { type ColRefScope, colRefKind } from '../col-ref-kind.ts';
import { colNodeLabels, projectColumnRef } from '../column-ref.ts';
import {
  CorrelatedSubqueryError,
  EmptySetRefError,
  SetRefTooLargeError,
  StructSetRefError,
  UncollapsibleRefError,
} from '../errors.ts';
import { INTERN_THRESHOLD, type InternedValue, internedSetKey } from '../interning.ts';
import { type AstInput, astShape, extractLambdaParams, type OpCall } from '../normalize.ts';
import { cellKey } from '../runtime/cell-key.ts';
import type { IdentityTable, RowIdentityType } from '../schema/row-identity.ts';
import { type ColumnType, identityElement, type Schema, structPathType, unwrapNullable } from '../schema/types.ts';
import type { Session } from '../session/Session.ts';
import {
  type ColumnRef,
  Dataset,
  expectColumnRef,
  isMaterialized,
  type MaterializedDataset,
  type Value,
} from '../value.ts';
import { rowColLabels } from './decode.ts';
import { collapseToValues, genericCollapseColumn } from './generic-stage.ts';
import { KNOWLEDGE_GRAPH_GROUP, SOURCE_OP_NAMES, TIMESERIES_GROUP } from './groups.ts';
import type { DatasetProvider } from './types.ts';
import type { ProviderResolver } from './useProviderRegistry.ts';

/**
 * The capability bundle the pass needs — evaluation + materialisation seams,
 * the scope predicate that decides whether a `(col …)` head names a bound
 * dataset, the provider resolver (a source op's name → its `collapseRef`
 * rule), and the source position for diagnostics. Constant across the walk;
 * the lexical row-alias frame and enclosing provider are threaded internally.
 */
export type SetRefDeps = {
  /** Evaluate a sub-node through the engine — to force an inner `(query …)` or
   *  resolve a foreign `(col …)` to its `ColumnRef`. Pre-bound to the form's env. */
  readonly evalNode: (node: AstInput) => Promise<Value>;
  /** Force a (possibly lazy) inner dataset to a `MaterializedDataset`. */
  readonly materialize: (ds: Dataset) => Promise<MaterializedDataset>;
  /** Does `name` resolve to a dataset bound in scope (a `(def …)` / session
   *  binding, or an env-bound `Dataset`)? */
  readonly isBoundDataset: (name: string) => boolean;
  /** Resolve a source op's `(group, name)` to the provider whose `collapseRef`
   *  rule shapes a collapsed operand. Absent ⇒ the generic collapse fallback. */
  readonly getProvider?: ProviderResolver;
  /** Conversation-scoped session — interns a large inlined identity vector
   *  behind a small content handle so it does not bloat the source cache key. */
  readonly session: Session;
  /** Composed row-identity contracts — keys an interned identity vector by
   *  `identity.key` so the handle is content-derived and order-insensitive. */
  readonly identities: IdentityTable;
  /** Source position for diagnostics. */
  readonly pos: SourcePos;
};

/** Threaded walk context: the lexical row-alias frame, the enclosing source
 *  op's provider, and that source op's declared output schema (so a comparison
 *  can type its row column and recognise a relation-membership LHS).
 *  (`rowAliases` is empty while in the structural skeleton — it is only
 *  populated once an `fn` body is entered.) */
type Ctx = {
  readonly rowAliases: ReadonlySet<string>;
  readonly provider: DatasetProvider | undefined;
  readonly sourceSchema: Schema | undefined;
  readonly deps: SetRefDeps;
};

const EMPTY_ALIASES: ReadonlySet<string> = new Set();

/** Comparison / membership leaf heads whose operands may carry a set value. */
const COMPARISON_NAMES: ReadonlySet<string> = new Set(['=', '!=', 'in', '<', '<=', '>', '>=']);

/**
 * Reduce a column's materialised cells to the structurally-distinct value set
 * inlined as an `in`-style literal vector, enforcing the two invariants any
 * pushed/inlined set must satisfy:
 *
 *   - **non-empty** — an empty set inlined as an `in` matches nothing, almost
 *     always an upstream mistake (`EmptySetRefError`);
 *   - **bounded** — capped at `SET_REF_MAX` so the inlined list stays within
 *     CDF's instance-filter size limit (`SetRefTooLargeError`).
 *
 * Distinctness is structural (`cellKey`) so direct-relation
 * `{space, externalId}` structs dedup by value, not reference, and a
 * unit-boxed number dedups with its plain equal.
 */
export const boundedDistinctSet = (cells: readonly unknown[], pos: SourcePos): readonly AstInput[] => {
  const unique = Array.from(new Map(cells.map((v) => [cellKey(v), v] as const)).values());
  if (unique.length === 0) {
    throw new EmptySetRefError(pos);
  }
  if (unique.length > SET_REF_MAX) {
    throw new SetRefTooLargeError(unique.length, SET_REF_MAX, pos);
  }
  return unique as readonly AstInput[];
};

/**
 * Build the inlined instance-ref set for a relation set-ref target. A
 * `{space, externalId}` instance ref is a runtime VALUE the engine injects into
 * the AST — `astShape` classifies it as an opaque `embedded` value (a
 * self-evaluating non-marker object), and the pushdown's `isInstanceRefOrVector`
 * guard selects it on exactly that basis. This is the SINGLE sanctioned site
 * that lifts an instance-ref array into the `AstInput` surface, so the
 * `InstanceIdType`→`AstInput` reconciliation lives in one place rather than
 * scattered `as AstInput` casts.
 *
 * `refs` is the row-aligned identity axis: per-row `undefined` (a row with no
 * determinate instance identity) is filtered out before the distinct set is
 * built, so it never contributes a spurious member.
 *
 * INTERNING: a distinct set at/above `INTERN_THRESHOLD` is registered in the
 * session intern map under its content-derived handle and the AST carries the
 * small `InternedValue` handle (itself an opaque `embedded` value) instead of
 * the full vector — so the source cache key stays small and the handle resolves
 * back to the real refs at translate/fetch time. A smaller set stays inline.
 */
export const instanceRefSet = (refs: readonly (InstanceIdType | undefined)[], deps: SetRefDeps): AstInput => {
  const present = refs.filter((ref) => ref !== undefined);
  const vector = boundedDistinctSet(present, deps.pos);
  const identity = vector.length > 0 ? deps.identities.claimingIdentity(vector[0]) : undefined;
  if (vector.length < INTERN_THRESHOLD || identity === undefined) {
    return vector as AstInput;
  }
  const key = internedSetKey(vector, identity);
  deps.session.intern(key, vector);
  return { $interned: key } satisfies InternedValue;
};

/**
 * Inline every row-independent cross-dataset set reference in `node` to a
 * literal value/vector. The single entry point, run per top-level form by
 * `evalScript`. Begins in the structural skeleton; a form with no value
 * expression (or none referencing a foreign dataset) returns (structurally)
 * unchanged — the common case is a cheap walk.
 */
export const inlineSetRefs = (node: AstInput, deps: SetRefDeps): Promise<AstInput> =>
  descend(node, { rowAliases: EMPTY_ALIASES, provider: undefined, sourceSchema: undefined, deps });

/**
 * Recurse a vector's elements with `recurse`, preserving referential identity
 * when nothing changed. Shared by both walks so they differ only in dispatch.
 */
const mapVector = async (
  elements: readonly AstInput[],
  node: AstInput,
  recurse: (n: AstInput) => Promise<AstInput>,
): Promise<AstInput> => {
  const resolved = await Promise.all(elements.map(recurse));
  return resolved.some((el, i) => el !== elements[i]) ? (resolved as AstInput) : node;
};

/**
 * Recurse an op-call's params with `recurse`, rebuilding the call only when a
 * child changed (preserving referential identity otherwise).
 */
const mapParams = async (
  call: OpCall,
  params: readonly AstInput[],
  node: AstInput,
  recurse: (n: AstInput) => Promise<AstInput>,
): Promise<AstInput> => {
  const resolved = await Promise.all(params.map(recurse));
  return resolved.some((p, i) => p !== params[i]) ? ({ ...call, params: resolved } as AstInput) : node;
};
/**
 * Structural skeleton walk. Traverses op pipelines, source-op trailing forms,
 * and join structure WITHOUT resolving any `(col …)` — those are column
 * identifiers here. Its only job is to reach value expressions: it hands an
 * `fn` body to `resolveExpr` and sets the enclosing provider on a source op.
 */
const descend = async (node: AstInput, ctx: Ctx): Promise<AstInput> => {
  const shape = astShape(node);

  if (shape.kind === 'vector') {
    return mapVector(shape.elements, node, (n) => descend(n, ctx));
  }

  if (shape.kind !== 'opCall') {
    return node;
  }

  const { call } = shape;
  const params = (call.params ?? []) as readonly AstInput[];

  // An `fn` body is THE boundary into value context.
  if (call.name === 'fn') {
    return resolveLambda(call, params, node, ctx);
  }

  // A source op opens its own pipeline under its provider; the value-collapse
  // rule for refs inside it is provider-scoped, and its declared schema types
  // the row columns a nested comparison filters on. Only the knowledge-graph
  // `query` op surfaces relation columns, and the CDF provider's `schema` is
  // parameter-free; `getProvider` already returns the provider that CLAIMS the
  // named view, so the no-param call is total.
  if (SOURCE_OP_NAMES.has(call.name)) {
    const provider = sourceProvider(call, params, ctx.deps);
    const sourceSchema =
      call.name === 'query' && typeof params[0] === 'string' && provider !== undefined
        ? provider.schema(params[0], undefined)
        : undefined;
    const childCtx: Ctx = { ...ctx, provider, sourceSchema };
    return mapParams(call, params, node, (n) => descend(n, childCtx));
  }

  // Any other op (filter/derive/join/columns/…): keep descending. A `(col …)`
  // reached here is a structural identifier (a join key) — left untouched,
  // because only `resolveExpr` resolves references.
  return mapParams(call, params, node, (n) => descend(n, ctx));
};

/** Resolve an `fn` body in value context, pushing the lambda's params onto the
 *  row-alias frame. THE bridge from structural into value state — called for
 *  every `fn`, whether reached from the skeleton (a top-level predicate) or
 *  from within another expression (a nested lambda). The param vector
 *  (`params[0]`) stays structural. */
const resolveLambda = async (
  call: OpCall,
  params: readonly AstInput[],
  node: AstInput,
  ctx: Ctx,
): Promise<AstInput> => {
  const lambdaParams = extractLambdaParams(node) ?? [];
  const childCtx: Ctx = { ...ctx, rowAliases: new Set([...ctx.rowAliases, ...lambdaParams]) };
  const [paramVec, ...body] = params;
  const resolvedBody = await Promise.all(body.map((p) => resolveExpr(p, childCtx)));
  return { ...call, params: [paramVec, ...resolvedBody] } as AstInput;
};

/** The provider whose `collapseRef` rule shapes a dataset operand collapsed
 *  inside this source op's pipeline — keyed off the source op's name. */
const sourceProvider = (call: OpCall, params: readonly AstInput[], deps: SetRefDeps): DatasetProvider | undefined => {
  if (call.name === 'query') {
    return typeof params[0] === 'string' ? deps.getProvider?.(KNOWLEDGE_GRAPH_GROUP, params[0]) : undefined;
  }
  return deps.getProvider?.(TIMESERIES_GROUP, 'datapoints');
};

/**
 * Value expression walk. Resolves every cross-dataset reference within an `fn`
 * body: a foreign `(col …)` inlines to its value set; a binary comparison may
 * collapse a dataset operand and forgive `=`/`!=` to membership; a nested `fn`
 * pushes its params; a nested source op hands back to `descend` (its pipeline
 * is structural again, until its own `fn`).
 */
const resolveExpr = async (node: AstInput, ctx: Ctx): Promise<AstInput> => {
  const shape = astShape(node);

  if (shape.kind === 'vector') {
    return mapVector(shape.elements, node, (n) => resolveExpr(n, ctx));
  }

  if (shape.kind !== 'opCall') {
    return node;
  }

  const { call } = shape;
  const params = (call.params ?? []) as readonly AstInput[];

  if (call.name === 'col') {
    // A foreign ref (head names a bound dataset) resolves to a whole-dataset
    // set without a row in context; a row / bare-column / expression-headed
    // ref is left for the per-row compiler. In value position there is no
    // paired operand to mirror a struct collapse against, so the flag is
    // dropped — only `resolveComparison` projects the row side.
    if (colRefKind(colNodeLabels(node), colScope(ctx)) === 'foreign') {
      return inlineForeignCol(node, ctx);
    }
    return node;
  }

  if (call.name === 'fn') {
    return resolveLambda(call, params, node, ctx);
  }

  // A source op in value position is a sub-expression yielding a dataset (e.g.
  // a bare sub-query); its own pipeline is structural again.
  if (SOURCE_OP_NAMES.has(call.name)) {
    return descend(node, ctx);
  }

  if (COMPARISON_NAMES.has(call.name) && params.length === 2) {
    return resolveComparison(call, params[0] as AstInput, params[1] as AstInput, ctx);
  }

  // Any other op (boolean combinator, arithmetic, aggregate, …): resolve every
  // operand in value context. This is what makes a foreign ref valid wherever a
  // value is — nested in an aggregate input or a derive expression, not just a
  // filter predicate.
  return mapParams(call, params, node, (n) => resolveExpr(n, ctx));
};

const colScope = (ctx: Ctx): ColRefScope => ({ rowAliases: ctx.rowAliases, isBoundDataset: ctx.deps.isBoundDataset });

/** A relation target leaf — a direct relation (a `row-identity` column), a
 *  HYDRATED relation (`hydrated-relation`, identity-bearing with walkable
 *  fields), or a LIST of either. Identity-ness lives in the TYPE KIND and is
 *  resolved through the single {@link identityElement} SSOT — never a struct
 *  field-name sniff, so an arbitrary struct is still NOT a relation. The
 *  membership set is resolved to FULL {space, externalId} instance refs, not
 *  bare externalId strings, so the row operand compares identity-vs-identity and
 *  the pushdown can push the set as `containsAny` instance refs. Each cell's own
 *  `space` is then required by {@link toInstanceRef} (never assumed). A hydrated
 *  cell carries extra fields, which `toInstanceRef` simply ignores. */
const relationElement = (leaf: ColumnType | undefined): { readonly identity: RowIdentityType } | undefined => {
  if (leaf === undefined) {
    return undefined;
  }
  const u = unwrapNullable(leaf);
  const element = u.kind === 'array' ? u.element : u;
  return identityElement(element);
};

/** Shape one relation cell (a `{space, externalId}` struct) into a full
 *  instance ref, using the cell's OWN `space` — a relation target space is
 *  never assumed. A `null` cell is dropped (no relation target); a cell missing
 *  its own `space` or `externalId` FAILS LOUD. */
const toInstanceRef = (cell: unknown, pos: SourcePos): InstanceIdType | undefined => {
  if (cell === null || cell === undefined) {
    return undefined;
  }
  const { space, externalId } = cell as { space?: unknown; externalId?: unknown };
  if (typeof space !== 'string' || typeof externalId !== 'string') {
    throw new StructSetRefError(
      'cannot resolve a relation set-ref to instance refs — a target cell lacks its own `space` ' +
        'or `externalId` (a space is never assumed)',
      pos,
    );
  }
  return { space, externalId };
};

/**
 * Project a foreign set-ref column's cells, union-flattening one level for an
 * ARRAY column (a relation LIST, or a plain `array<scalar>` such as an
 * `externalId` walk over an array-relation, `tags`, `aliases`) so a
 * column-of-arrays resolves to the SET of its elements — the membership
 * semantics both the relation and scalar branches share. A non-array column's
 * cells are returned unchanged.
 */
const projectSetCells = async (
  ref: ColumnRef,
  unwrapped: ColumnType | undefined,
  deps: SetRefDeps,
): Promise<readonly unknown[]> => {
  const { cells } = await projectColumnRef(ref, deps.pos, deps.materialize);
  return unwrapped?.kind === 'array' ? cells.flatMap((cell) => (Array.isArray(cell) ? cell : [])) : cells;
};

/**
 * Resolve a foreign `(col other-ds column [path…])` to its bounded distinct
 * value set — reusing `col`'s own apply for Env→Session resolution + schema
 * validation, then projecting + materialising the column via
 * {@link projectSetCells}.
 *
 * A RELATION target (a direct-relation struct, or a LIST of them) resolves to
 * FULL {space, externalId} instance refs: each cell is shaped via
 * {@link toInstanceRef} using the cell's OWN space (never assumed). The row
 * operand is then compared struct-vs-struct (no `externalId` collapse), which
 * the pushdown pushes as `containsAny`. A plain `array<scalar>` column resolves
 * to the union of its element values. A `timeseries` / `nested-dataset` column
 * — or a struct with no `externalId` field — has no relation/scalar projection
 * and fails with a typed, actionable {@link StructSetRefError}.
 */
const inlineForeignCol = async (node: AstInput, ctx: Ctx): Promise<AstInput> => {
  const value = await ctx.deps.evalNode(node);
  const ref = expectColumnRef(value, '(col …) cross-dataset reference', ctx.deps.pos);
  const leaf = structPathType(ref.ds.schema.get(ref.column), ref.path);
  const unwrapped = leaf === undefined ? undefined : unwrapNullable(leaf);

  const relation = relationElement(leaf);
  if (relation !== undefined) {
    const refs = (await projectSetCells(ref, unwrapped, ctx.deps)).flatMap((cell) => {
      const instanceRef = toInstanceRef(cell, ctx.deps.pos);
      return instanceRef === undefined ? [] : [instanceRef];
    });
    return instanceRefSet(refs, ctx.deps);
  }

  if (unwrapped?.kind === 'struct') {
    throw new StructSetRefError(
      `cannot inline struct column '${ref.column}' as a comparison set — it has no 'externalId' field`,
      ctx.deps.pos,
    );
  }

  if (unwrapped?.kind === 'timeseries' || unwrapped?.kind === 'nested-dataset') {
    throw new StructSetRefError(
      `cannot inline ${unwrapped.kind} column '${ref.column}' as a comparison set — its cells are not scalar values`,
      ctx.deps.pos,
    );
  }

  return boundedDistinctSet(await projectSetCells(ref, unwrapped, ctx.deps), ctx.deps.pos) as AstInput;
};

/**
 * Resolve a binary comparison `(op lhs rhs)`. At most one operand is a set
 * value — either an inline dataset sub-query (materialised once + collapsed)
 * or a foreign `(col …)` set-ref (inlined as a vector); the other is the row
 * side. `=`/`!=` against a SET is forgiven to `in`/`not in` membership; an
 * already-`in` head or an ordered comparison keeps the original operand order.
 *
 * A RELATION-membership comparison whose row operand is the BARE relation
 * `(col % assets)` or a LIST relation resolves its set to FULL `{space,
 * externalId}` instance identities read directly from the SOURCE dataset's typed
 * identity axis, so the relation target space is never assumed (identity-vs-
 * identity). A SINGLE direct relation addressed by its `externalId` sub-path
 * (`(col % asset externalId)`) instead compares scalar-vs-scalar via the default
 * collapse. Any other set-ref keeps the prior collapse.
 */
const resolveComparison = async (call: OpCall, lhs: AstInput, rhs: AstInput, ctx: Ctx): Promise<AstInput> => {
  const name = call.name;

  // The row operand of a relation-membership filter that resolves its set to
  // FULL `{space, externalId}` identities: the BARE relation `(col % rel)`
  // (identity-vs-identity) or a LIST relation. A SINGLE direct relation
  // addressed by a scalar sub-path — `(col % rel externalId)` — is deliberately
  // excluded: its per-row value is the scalar externalId, so it falls through to
  // the default scalar-set collapse (which inlines the foreign `externalId`
  // column as strings) instead of full-ref recovery, which would leave the
  // residual `in` comparing scalar-vs-ref. List relations keep full refs,
  // matching the enriched ancestor-scope pushdown.
  const rowIsRelation = (rowNode: AstInput): boolean => {
    const labels = rowColLabels(rowNode);
    const base = labels?.[0];
    if (labels === undefined || base === undefined) {
      return false;
    }
    const leaf = ctx.sourceSchema?.get(base);
    if (leaf === undefined || relationElement(leaf) === undefined) {
      return false;
    }
    return unwrapNullable(leaf).kind === 'array' || labels.length === 1;
  };

  // A materialised source's full {space, externalId} node identity as the
  // membership set, read directly from the source dataset's typed identity
  // axis. Fails loud (never assumes a space) when the source carries no node
  // identity.
  const identitySet = (source: MaterializedDataset): AstInput => {
    const refs = source.identity;
    if (refs === undefined) {
      throw new StructSetRefError(
        'cannot resolve a relation-membership set-ref to instance refs — the source dataset has no ' +
          'node identity {space, externalId} to take the target space from (a space is never assumed)',
        ctx.deps.pos,
      );
    }
    return instanceRefSet(refs, ctx.deps);
  };

  // The foreign set for a relation-membership row, resolved to full instance
  // identities. A foreign RELATION-column projection already carries both
  // fields per cell (inlineForeignCol); any other projection (the agent's
  // `.externalId`) lost the space, so recover the foreign instance's own full
  // node identity from the source.
  const relationSet = async (setNode: AstInput): Promise<AstInput> => {
    const ref = expectColumnRef(await ctx.deps.evalNode(setNode), '(col …) cross-dataset reference', ctx.deps.pos);
    const leaf = structPathType(ref.ds.schema.get(ref.column), ref.path);
    return relationElement(leaf) === undefined
      ? identitySet(await ctx.deps.materialize(ref.ds))
      : inlineForeignCol(setNode, ctx);
  };

  const rhsDataset = await datasetOperand(rhs, ctx);
  if (rhsDataset !== undefined) {
    const collapsed = rowIsRelation(lhs)
      ? identitySet(rhsDataset)
      : collapseDatasetOperand(rhs, lhsColumn(lhs), rhsDataset, ctx);
    const row = await resolveExpr(lhs, ctx);
    return inlineCollapsed(name, row, collapsed, [row, collapsed]);
  }
  const lhsDataset = await datasetOperand(lhs, ctx);
  if (lhsDataset !== undefined) {
    const collapsed = rowIsRelation(rhs)
      ? identitySet(lhsDataset)
      : collapseDatasetOperand(lhs, lhsColumn(rhs), lhsDataset, ctx);
    const row = await resolveExpr(rhs, ctx);
    return inlineCollapsed(name, row, collapsed, [collapsed, row]);
  }

  // No sub-query operand. At most one side is a foreign `(col …)` set-ref; the
  // other is the row side.
  const lhsForeign = isForeignColRef(lhs, ctx);
  const rhsForeign = isForeignColRef(rhs, ctx);

  if (lhsForeign !== rhsForeign) {
    const [setNode, rowNode] = lhsForeign ? [lhs, rhs] : [rhs, lhs];
    const setValue = rowIsRelation(rowNode) ? await relationSet(setNode) : await inlineForeignCol(setNode, ctx);
    const row = await resolveExpr(rowNode, ctx);
    return inlineCollapsed(name, row, setValue, lhsForeign ? [setValue, row] : [row, setValue]);
  }

  // Neither operand is a foreign set-ref: resolve both in value context.
  const resolved = [await resolveExpr(lhs, ctx), await resolveExpr(rhs, ctx)] as const;
  return { ...call, params: [resolved[0], resolved[1]] } as AstInput;
};

/** Whether `node` is a foreign `(col other-ds …)` set-ref under this scope. */
const isForeignColRef = (node: AstInput, ctx: Ctx): boolean => {
  const s = astShape(node);
  return s.kind === 'opCall' && s.call.name === 'col' && colRefKind(colNodeLabels(node), colScope(ctx)) === 'foreign';
};

/**
 * Build the inlined comparison, applying `=`/`!=` → `in`/`not in` forgiveness
 * when the collapsed value is a SET (vector). For every other head — `in`
 * already, or an ordered comparison — the collapsed literal is inlined IN
 * PLACE (`positioned`), preserving the original operand order.
 */
const inlineCollapsed = (
  name: string,
  row: AstInput,
  collapsed: AstInput,
  positioned: readonly AstInput[],
): AstInput => {
  if (Array.isArray(collapsed) && (name === '=' || name === '!=')) {
    const membership = { name: 'in', params: [row, collapsed] };
    return (name === '=' ? membership : { name: 'not', params: [membership] }) as AstInput;
  }
  return { name, params: positioned } as AstInput;
};

/**
 * Materialise a comparison operand if it is a dataset-valued sub-query, else
 * `undefined`. Two carrier shapes: a bare source-op call (`(query …)`) whose
 * whole result is collapsed, or an explicit `(col <inner-query> <name>)`
 * projection whose head is a source-op call. The inner query's OWN pipeline is
 * resolved first (via `descend`, so it materialises against literals), and the
 * correlated-reference guard fires before evaluation for a precise diagnostic.
 */
const datasetOperand = async (operand: AstInput, ctx: Ctx): Promise<MaterializedDataset | undefined> => {
  const innerNode = subQueryNode(operand);
  if (innerNode === undefined) {
    return undefined;
  }
  const correlated = correlatedAlias(innerNode, ctx.rowAliases);
  if (correlated !== undefined) {
    throw new CorrelatedSubqueryError(correlated, ctx.deps.pos);
  }
  const resolvedInner = await descend(innerNode, ctx);
  const value = await ctx.deps.evalNode(resolvedInner);
  if (!(value instanceof Dataset)) {
    return undefined;
  }
  return isMaterialized(value) ? value : ctx.deps.materialize(value);
};

/**
 * The dataset-producing sub-query node carried by a comparison operand, or
 * `undefined`. A bare source-op call is its own inner query; a `(col <head> …)`
 * whose head is a source-op call carries the inner query in that head. A
 * `(col …)` with a literal head (`%` row ref or a binding-name set-ref) is not
 * a sub-query — those are the foreign-col path / per-row residual.
 */
const subQueryNode = (operand: AstInput): AstInput | undefined => {
  const shape = astShape(operand);
  if (shape.kind !== 'opCall') {
    return undefined;
  }
  if (SOURCE_OP_NAMES.has(shape.call.name)) {
    return operand;
  }
  if (shape.call.name !== 'col') {
    return undefined;
  }
  const head = (shape.call.params ?? [])[0] as AstInput | undefined;
  const headShape = head === undefined ? undefined : astShape(head);
  return headShape?.kind === 'opCall' && SOURCE_OP_NAMES.has(headShape.call.name) ? head : undefined;
};

/** The LHS column name a collapse rule keys on — the row column the dataset
 *  operand is compared against, or `undefined` for a complex LHS. */
const lhsColumn = (lhs: AstInput): string | undefined => {
  const labels = rowColLabels(lhs);
  return labels?.length === 1 ? labels[0] : undefined;
};

/**
 * Collapse a materialised inner dataset to the literal vector to inline. A
 * provider `collapseRef` (keyed on the outer `lhsCol`) owns both column choice
 * AND value shaping; absent that, the generic rule picks an explicit
 * `(col <inner> <name>)` projection or a single-column inner result, else
 * `UncollapsibleRefError`, after a type-shape guard rejects a nested/struct/TS
 * column.
 */
const collapseDatasetOperand = (
  operandNode: AstInput,
  lhsCol: string | undefined,
  inner: MaterializedDataset,
  ctx: Ctx,
): AstInput => {
  const collapseRef = ctx.provider?.collapseRef;
  if (collapseRef !== undefined && lhsCol !== undefined) {
    return collapseRef(lhsCol, inner);
  }
  const column = genericCollapseColumn(operandNode, inner, ctx.deps.pos);
  if (!isInlinableColumnType(inner.schema, column)) {
    throw new UncollapsibleRefError([column], ctx.deps.pos);
  }
  return boundedDistinctSet(collapseToValues(inner, column), ctx.deps.pos) as AstInput;
};

/** Column types that can be inlined as a scalar comparison value. A
 *  nested-dataset / struct / timeseries cell cannot. */
const isInlinableColumnType = (schema: Schema, column: string): boolean => {
  const type: ColumnType | undefined = schema.get(column);
  if (type === undefined) {
    return false;
  }
  const kind = type.kind === 'nullable' ? type.inner.kind : type.kind;
  return kind !== 'nested-dataset' && kind !== 'struct' && kind !== 'timeseries';
};

/**
 * If a sub-query node references an enclosing row alias outside any inner
 * lambda that shadows it, return the offending alias (correlated); else
 * `undefined`. Scoping is LEXICAL: an inner lambda's params are dropped from
 * the in-scope set for that lambda's body, so a shadowing inner `%` is fine.
 * `inScopeRoot` is the UNION of every enclosing lambda frame, so a correlation
 * to a grand-outer alias is caught, not only the immediately-enclosing one.
 */
const correlatedAlias = (node: AstInput, inScopeRoot: ReadonlySet<string>): string | undefined => {
  let found: string | undefined;
  const visit = (n: AstInput, inScope: ReadonlySet<string>): void => {
    if (found !== undefined || inScope.size === 0) {
      return;
    }
    const shape = astShape(n);
    if (shape.kind === 'symbolRef') {
      if (inScope.has(shape.sym)) {
        found = shape.sym;
      }
      return;
    }
    if (shape.kind === 'vector') {
      shape.elements.forEach((el) => {
        visit(el, inScope);
      });
      return;
    }
    if (shape.kind !== 'opCall') {
      return;
    }
    const head = colNodeLabels(n)?.[0];
    if (head !== undefined && inScope.has(head)) {
      found = head;
      return;
    }
    const lambdaParams = shape.call.name === 'fn' ? extractLambdaParams(n) : undefined;
    const bodyScope =
      lambdaParams === undefined ? inScope : new Set([...inScope].filter((a) => !lambdaParams.includes(a)));
    (shape.call.params ?? []).forEach((p) => {
      visit(p as AstInput, bodyScope);
    });
  };
  visit(node, inScopeRoot);
  return found;
};
